import React, { useEffect, useMemo, useState } from "react";
import { addFee, deleteFee, getClasses, getFees, getStudents, updateFee } from "../services/api";
import DateInput from "../components/DateInput";
import { confirmAppAction, showAppToast } from "../utils/feedback";
import { formatDate, toInputDate } from "../utils/helpers";
import "./FinancePage.css";

const emptyFeeForm = {
  student_id: "",
  billingCycle: "monthly",
  amount: "",
  dueDate: "",
  paidDate: "",
  status: "unpaid",
  notes: ""
};

const getTodayDate = () => new Date().toISOString().slice(0, 10);

const toNumber = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatAmount = (value) =>
  new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 0
  }).format(toNumber(value));

const normalizeCourse = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) {
    return "";
  }

  return trimmed === "Fees" ? "General" : trimmed;
};

function FeesPage() {
  const [fees, setFees] = useState([]);
  const [allFees, setAllFees] = useState([]);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState("all");
  const [selectedBoard, setSelectedBoard] = useState("all");
  const [selectedCourse, setSelectedCourse] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedCycle, setSelectedCycle] = useState("all");
  const [formData, setFormData] = useState(emptyFeeForm);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    fetchClasses();
    fetchStudents();
  }, []);

  useEffect(() => {
    fetchFees();
    fetchStudents();
  }, [selectedClass, selectedBoard, selectedCourse, selectedStatus, selectedCycle]);

  const fetchClasses = async () => {
    const res = await getClasses();
    setClasses(res.data);
  };

  const fetchStudents = async () => {
    const res = await getStudents(selectedClass, { fields: "lookup" });
    setStudents(res.data);
  };

  const fetchFees = async () => {
    const [filteredRes, classLedgerRes] = await Promise.all([
      getFees({
        class: selectedClass,
        board: selectedBoard,
        course: selectedCourse,
        status: selectedStatus,
        billingCycle: selectedCycle
      }),
      getFees({
        class: selectedClass,
        board: selectedBoard,
        course: selectedCourse
      })
    ]);
    setFees(filteredRes.data);
    setAllFees(classLedgerRes.data);
  };

  const filteredStudents = useMemo(() => students.filter((student) => {
    const matchesBoard = selectedBoard === "all" || (student.board || "") === selectedBoard;
    const matchesCourse = selectedCourse === "all" || normalizeCourse(student.course) === selectedCourse;
    return matchesBoard && matchesCourse;
  }), [students, selectedBoard, selectedCourse]);

  const boardOptions = useMemo(() => Array.from(
    new Set(students.map((student) => (student.board || "").trim()).filter(Boolean))
  ).sort(), [students]);

  const courseOptions = ["Science", "Commerce", "General"];

  const studentById = useMemo(
    () => new Map(students.map((student) => [String(student._id), student])),
    [students]
  );

  const getStudentById = (studentId) => studentById.get(String(studentId));

  const getStudentTotalFees = (studentId) => toNumber(getStudentById(studentId)?.fees);

  const paidAmountByStudent = useMemo(() => {
    const totals = new Map();
    allFees.forEach((record) => {
      if (record.status !== "paid") {
        return;
      }

      const studentId = String(record.student_id);
      totals.set(studentId, (totals.get(studentId) || 0) + toNumber(record.amount));
    });
    return totals;
  }, [allFees]);

  const getPaidAmountForStudent = (studentId, excludeRecordId = null) => {
    if (!excludeRecordId) {
      return paidAmountByStudent.get(String(studentId)) || 0;
    }

    return allFees
      .filter(
        (record) =>
          String(record.student_id) === String(studentId) &&
          record.status === "paid" &&
          String(record._id) !== String(excludeRecordId)
      )
      .reduce((sum, record) => sum + toNumber(record.amount), 0);
  };

  const getRemainingBalanceForStudent = (studentId, excludeRecordId = null) =>
    Math.max(getStudentTotalFees(studentId) - getPaidAmountForStudent(studentId, excludeRecordId), 0);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "student_id") {
      const remainingBalance = getRemainingBalanceForStudent(value, editingId);
      setFormData((current) => ({
        ...current,
        student_id: value,
        amount: current.amount || (remainingBalance ? String(remainingBalance) : "")
      }));
      return;
    }

    if (name === "status" && value === "paid" && !formData.paidDate) {
      setFormData({ ...formData, status: value, paidDate: getTodayDate() });
      return;
    }

    setFormData({ ...formData, [name]: value });
  };

  const resetForm = () => {
    setFormData(emptyFeeForm);
    setEditingId(null);
    setShowForm(false);
  };

  const handleSaveFee = async () => {
    if (!formData.student_id || !formData.amount || !formData.dueDate) {
      showAppToast({
        type: "error",
        title: "Missing fee details",
        message: "Please select a student, amount, and due date before saving."
      });
      return;
    }

    const selectedStudentTotalFees = getStudentTotalFees(formData.student_id);
    const remainingBalance = getRemainingBalanceForStudent(formData.student_id, editingId);
    const enteredAmount = toNumber(formData.amount);

    if (formData.status === "paid" && selectedStudentTotalFees <= 0) {
      showAppToast({
        type: "error",
        title: "Total fees missing",
        message: "Please add the student's total fees in Student List before marking a paid amount."
      });
      return;
    }

    if (formData.status === "paid" && enteredAmount > remainingBalance) {
      showAppToast({
        type: "error",
        title: "Amount exceeds balance",
        message: `Remaining fee balance is ${formatAmount(remainingBalance)}. Please enter an amount within this balance.`
      });
      return;
    }

    const payload = {
      ...formData,
      amount: enteredAmount
    };

    try {
      if (editingId) {
        await updateFee(editingId, payload);
        showAppToast({
          type: "success",
          title: "Fee record updated",
          message: "The fee payment details were updated successfully."
        });
      } else {
        await addFee(payload);
        showAppToast({
          type: "success",
          title: "Fee record added",
          message: "The fee record was created successfully."
        });
      }

      resetForm();
      fetchFees();
      fetchStudents();
    } catch (err) {
      showAppToast({
        type: "error",
        title: "Failed to save fee record",
        message: err.response?.data?.error || err.message
      });
    }
  };

  const handleEdit = (fee) => {
    setFormData({
      student_id: fee.student_id,
      billingCycle: fee.billingCycle,
      amount: String(fee.amount),
      dueDate: toInputDate(fee.dueDate),
      paidDate: toInputDate(fee.paidDate),
      status: fee.status,
      notes: fee.notes || ""
    });
    setEditingId(fee._id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    const confirmed = await confirmAppAction({
      title: "Delete fee record",
      message: "This fee record will be removed permanently.",
      confirmLabel: "Delete"
    });

    if (!confirmed) {
      return;
    }

    try {
      await deleteFee(id);
      showAppToast({
        type: "success",
        title: "Fee record deleted",
        message: "The fee record was removed successfully."
      });
      fetchFees();
      fetchStudents();
    } catch (err) {
      showAppToast({
        type: "error",
        title: "Failed to delete fee record",
        message: err.response?.data?.error || err.message
      });
    }
  };

  const selectedStudent = getStudentById(formData.student_id);
  const selectedStudentTotalFees = getStudentTotalFees(formData.student_id);
  const selectedStudentAlreadyPaid = getPaidAmountForStudent(formData.student_id, editingId);
  const selectedCurrentPaid = formData.status === "paid" ? toNumber(formData.amount) : 0;
  const selectedBalanceAfterSave = Math.max(
    selectedStudentTotalFees - selectedStudentAlreadyPaid - selectedCurrentPaid,
    0
  );
  const totalFeeAmount = useMemo(
    () => filteredStudents.reduce((sum, student) => sum + toNumber(student.fees), 0),
    [filteredStudents]
  );
  const paidAmount = allFees
    .filter((fee) => fee.status === "paid")
    .reduce((sum, fee) => sum + toNumber(fee.amount), 0);
  const pendingAmount = Math.max(totalFeeAmount - paidAmount, 0);

  return (
    <div className="finance-page">
      {/* <div className="finance-page__header">
        <h1>Fees Management</h1>
        <p>Track student fee plans across monthly, quarterly, half-yearly, and yearly billing cycles.</p>
      </div> */}

      <div className="finance-page__stats">
        <div className="finance-page__stat-card">
          <h3>Total Records</h3>
          <p>{fees.length}</p>
        </div>
        <div className="finance-page__stat-card">
          <h3>Total Fees</h3>
          <p>{formatAmount(totalFeeAmount)}</p>
        </div>
        <div className="finance-page__stat-card">
          <h3>Paid Amount</h3>
          <p>{formatAmount(paidAmount)}</p>
        </div>
        <div className="finance-page__stat-card">
          <h3>Pending Amount</h3>
          <p>{formatAmount(pendingAmount)}</p>
        </div>
      </div>

      <div className="finance-page__filters">
        <div className="finance-page__filter-card">
          <label>Class</label>
          <select value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)}>
            <option value="all">All Classes</option>
            {classes.map((cls) => (
              <option key={cls} value={cls}>{cls}</option>
            ))}
          </select>
        </div>
        <div className="finance-page__filter-card">
          <label>Status</label>
          <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
            <option value="all">All Statuses</option>
            <option value="paid">Paid</option>
            <option value="unpaid">Unpaid</option>
          </select>
        </div>
        <div className="finance-page__filter-card">
          <label>Board</label>
          <select value={selectedBoard} onChange={(e) => setSelectedBoard(e.target.value)}>
            <option value="all">All Boards</option>
            {boardOptions.map((board) => (
              <option key={board} value={board}>{board}</option>
            ))}
          </select>
        </div>
        <div className="finance-page__filter-card">
          <label>Course</label>
          <select value={selectedCourse} onChange={(e) => setSelectedCourse(e.target.value)}>
            <option value="all">All Courses</option>
            {courseOptions.map((course) => (
              <option key={course} value={course}>{course}</option>
            ))}
          </select>
        </div>
        <div className="finance-page__filter-card">
          <label>Billing Cycle</label>
          <select value={selectedCycle} onChange={(e) => setSelectedCycle(e.target.value)}>
            <option value="all">All Cycles</option>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="half-yearly">Half Yearly</option>
            <option value="yearly">Yearly</option>
          </select>
        </div>
      </div>

      <div className="finance-page__panel">
        {showForm && (
          <div className="finance-page__form">
            <h2>{editingId ? "Edit Fee Record" : "Add Fee Record"}</h2>
            <div className="finance-page__form-grid">
              <div>
                <label>Student</label>
                <select name="student_id" value={formData.student_id} onChange={handleInputChange}>
                  <option value="">Select Student</option>
                  {filteredStudents.map((student) => (
                    <option key={student._id} value={student._id}>
                      {student.name} ({student.class})
                    </option>
                  ))}
                </select>
              </div>
              {selectedStudent && (
                <div className="finance-page__adjustment-card finance-page__form-grid-full">
                  <div>
                    <span>Total Fees</span>
                    <strong>{formatAmount(selectedStudentTotalFees)}</strong>
                  </div>
                  <div>
                    <span>Already Paid</span>
                    <strong>{formatAmount(selectedStudentAlreadyPaid)}</strong>
                  </div>
                  <div>
                    <span>Current Paid</span>
                    <strong>{formatAmount(selectedCurrentPaid)}</strong>
                  </div>
                  <div>
                    <span>Balance After Save</span>
                    <strong>{formatAmount(selectedBalanceAfterSave)}</strong>
                  </div>
                </div>
              )}
              <div>
                <label>Billing Cycle</label>
                <select name="billingCycle" value={formData.billingCycle} onChange={handleInputChange}>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="half-yearly">Half Yearly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
              <div>
                <label>Amount</label>
                <input name="amount" type="number" value={formData.amount} onChange={handleInputChange} />
              </div>
              <div>
                <label>Due Date</label>
                <DateInput name="dueDate" value={formData.dueDate} onChange={handleInputChange} />
              </div>
              <div>
                <label>Paid Date</label>
                <DateInput name="paidDate" value={formData.paidDate} onChange={handleInputChange} />
              </div>
              <div>
                <label>Status</label>
                <select name="status" value={formData.status} onChange={handleInputChange}>
                  <option value="unpaid">Unpaid</option>
                  <option value="paid">Paid</option>
                </select>
              </div>
              <div className="finance-page__form-grid-full">
                <label>Notes</label>
                <textarea name="notes" value={formData.notes} onChange={handleInputChange} rows="3" />
              </div>
            </div>
            <div className="finance-page__form-actions">
              <button className="finance-page__primary-btn" onClick={handleSaveFee}>
                {editingId ? "Update Fee Record" : "Save Fee Record"}
              </button>
              <button className="finance-page__secondary-btn" onClick={resetForm}>Cancel</button>
            </div>
          </div>
        )}

        {!showForm && (
          <button className="finance-page__primary-btn" onClick={() => setShowForm(true)}>
            Add Fee Record
          </button>
        )}

        <div className="finance-page__table-wrap">
          <table className="finance-page__table">
            <thead>
              <tr>
                <th>Student</th>
                <th>Class</th>
                <th>Board</th>
                <th>Course</th>
                <th>Cycle</th>
                <th>Amount</th>
                <th>Total Fees</th>
                <th>Paid</th>
                <th>Balance</th>
                <th>Due Date</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {fees.map((fee) => {
                const student = getStudentById(fee.student_id);
                const rowTotalFees = getStudentTotalFees(fee.student_id);
                const rowPaidAmount = getPaidAmountForStudent(fee.student_id);
                const rowBalance = Math.max(rowTotalFees - rowPaidAmount, 0);

                return (
                  <tr key={fee._id}>
                    <td>{fee.student_name}</td>
                    <td>{fee.class}</td>
                    <td>{student?.board || "-"}</td>
                    <td>{normalizeCourse(student?.course) || "-"}</td>
                    <td>{fee.billingCycle}</td>
                    <td>{formatAmount(fee.amount)}</td>
                    <td>{formatAmount(rowTotalFees)}</td>
                    <td>{formatAmount(rowPaidAmount)}</td>
                    <td>{formatAmount(rowBalance)}</td>
                    <td>{formatDate(fee.dueDate)}</td>
                    <td>
                      <span className={`finance-page__badge finance-page__badge--${fee.status}`}>{fee.status}</span>
                    </td>
                    <td>
                      <button className="finance-page__inline-btn" onClick={() => handleEdit(fee)}>Edit</button>
                      <button className="finance-page__inline-btn finance-page__inline-btn--danger" onClick={() => handleDelete(fee._id)}>Delete</button>
                    </td>
                  </tr>
                );
              })}
              {fees.length === 0 && (
                <tr>
                  <td colSpan="12" className="finance-page__empty">No fee records found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default FeesPage;
